/**
 * Landlock 아이템 시스템 설정 (단일 소스)
 * 설계: landlock/docs/item_system_design.md
 */

export type ItemType =
  | 'invincible'
  | 'giant'
  | 'bomb'
  | 'mini'
  | 'slow'
  | 'reverse'
  | 'mystery'; // 보석 상자: 먹으면 자기 이득 버프 중 랜덤으로 변환

// 보석 상자가 변환될 수 있는 버프 풀 (자기 이득 버프만 — 디버프/폭탄 제외)
export const MYSTERY_BUFF_POOL: ItemType[] = ['invincible', 'giant', 'mini'];

/** 보석 상자(mystery)를 랜덤 버프로 치환. 그 외 종류는 그대로 둔다. */
export function rollMysteryTypes(types: ItemType[]): ItemType[] {
  return types.map((t) =>
    t === 'mystery'
      ? MYSTERY_BUFF_POOL[Math.floor(Math.random() * MYSTERY_BUFF_POOL.length)]
      : t
  );
}

export interface ItemData {
  id: string;
  type: ItemType;
  x: number;
  y: number;
}

// 지속효과 종류만 (폭탄 제외). 동시 획득 시 우선순위 비교용 — 인덱스가 낮을수록 우선.
export const EFFECT_PRIORITY: ItemType[] = [
  'invincible',
  'giant',
  'mini',
  'slow',
  'reverse',
];

export const ITEM = {
  durationMs: 5000, // 기본 지속시간 (효과별 미지정 시)

  // 효과별 지속시간 (ms). 미지정 효과는 durationMs를 따른다. (폭탄은 즉발이라 제외)
  durationByType: {
    invincible: 5000,
    giant: 10000,
    mini: 10000,
    slow: 10000,
    reverse: 7000,
  } as Partial<Record<ItemType, number>>,

  invincibleSpeedMul: 3,
  giantSizeMul: 3,
  giantSpeedMul: 1.5,
  miniSizeMul: 0.5,
  miniSpeedMul: 1.2,
  slowSpeedMul: 0.5,

  bomb: {
    missiles: 8,
    speed: 600, // px/s
    rangePx: 900,
  },

  spawn: {
    maxOnField: 7,
    intervalMs: 4000,
    expireMs: 25000,
  },

  // 종류별 스폰 가중치 (강버프는 드물게, 디버프가 과하지 않게)
  spawnWeights: {
    invincible: 1,
    giant: 2,
    bomb: 2,
    mini: 2,
    slow: 2,
    reverse: 2,
    mystery: 3, // 보석 상자 — 적당히 자주
  } as Record<ItemType, number>,
} as const;

// 종류별 표시 심볼/색 (Item 렌더 + 상태배지 공용)
export const ITEM_VISUAL: Record<
  ItemType,
  { symbol: string; ring: number; debuff: boolean }
> = {
  invincible: { symbol: '⭐', ring: 0xffe14e, debuff: false },
  giant: { symbol: '🔺', ring: 0xff8a3d, debuff: false },
  bomb: { symbol: '💣', ring: 0x495a73, debuff: false },
  mini: { symbol: '🐭', ring: 0x63e6be, debuff: false },
  slow: { symbol: '🐌', ring: 0xff6b6b, debuff: true },
  reverse: { symbol: '🔄', ring: 0xff6b6b, debuff: true },
  mystery: { symbol: '💎', ring: 0xb06bff, debuff: false },
};

// 획득 시 잠깐 떴다 사라지는 토스트 문구 (폭탄은 즉발이라 토스트 없음)
export const ITEM_TOAST: Record<ItemType, string | null> = {
  invincible: '⭐ 무적! 5초간 죽지 않고 3배 빨라져요',
  giant: '🔺 거대화! 10초간 몸집·속도가 커져 영역을 빠르게 넓혀요',
  bomb: null,
  mini: '🐭 축소! 10초간 작고 빨라져 적 사이를 빠져나가요',
  slow: '🐌 감속… 10초간 느려져요',
  reverse: '🔄 역방향! 7초간 조작이 반대로',
  mystery: null, // 변환된 버프의 토스트가 대신 표시된다
};

/**
 * 한 번의 점령으로 동시에 감싼 종류 배열에서
 * 발동할 (지속효과 1개, 폭탄 여부)를 산출한다.
 * - 폭탄은 우선순위와 별개로 포함되면 항상 발동.
 * - 지속효과는 EFFECT_PRIORITY 최상위 1개만.
 */
export function resolvePickup(types: ItemType[]): {
  effect: ItemType | null;
  bomb: boolean;
} {
  const bomb = types.includes('bomb');
  let effect: ItemType | null = null;
  for (const t of EFFECT_PRIORITY) {
    if (types.includes(t)) {
      effect = t;
      break;
    }
  }
  return { effect, bomb };
}

/** 가중치 기반 랜덤 종류 선택 */
export function randomItemType(): ItemType {
  const entries = Object.entries(ITEM.spawnWeights) as [ItemType, number][];
  const total = entries.reduce((sum, [, w]) => sum + w, 0);
  let r = Math.random() * total;
  for (const [type, w] of entries) {
    r -= w;
    if (r <= 0) return type;
  }
  return entries[0][0];
}
